import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, Ruler, Scissors, Handshake, Wrench, ShieldCheck, Factory, Star } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import Marquee from "@/components/site/Marquee";
import ProductCard from "@/components/site/ProductCard";
import Seo from "@/components/Seo";
import { useSettings } from "@/context/SettingsContext";

const ICONS = { truck: Truck, ruler: Ruler, scissors: Scissors, handshake: Handshake, wrench: Wrench, shield: ShieldCheck };

export default function Home() {
  const { settings } = useSettings();
  const [slides, setSlides] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [news, setNews] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get("/slides").then((r) => setSlides(r.data));
    api.get("/products?featured=true").then((r) => setProducts(r.data.slice(0, 8)));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/services").then((r) => setServices(r.data.slice(0, 4)));
    api.get("/news").then((r) => setNews(r.data.slice(0, 3)));
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setActive((a) => (a + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  const catName = (id) => categories.find((c) => c.id === id)?.name;
  const slide = slides[active];

  return (
    <div>
      <Seo title={settings?.seo_title || "Пром-Пластик — Завод ПВХ-панелей в Миассе"} description={settings?.seo_description || "Производство стеновых и потолочных ПВХ-панелей в Миассе. Прямые цены от завода."} image={slide?.image} />
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div key={active} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {slide?.label && <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0E7C86] flex items-center gap-2"><span className="w-8 h-px bg-[#0E7C86]" />{slide.label}</span>}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-800 tracking-tight text-[#0F1720] mt-4 leading-[1.05]">
              {slide?.title || "Стеновые панели ПВХ"}
            </h1>
            <p className="text-slate-600 text-base sm:text-lg mt-5 max-w-xl leading-relaxed">{slide?.subtitle}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to={slide?.cta_link || "/catalog"} data-testid="hero-cta-primary" className="inline-flex items-center gap-2 bg-[#E85D2A] hover:bg-[#D04A1B] text-white rounded-full px-7 py-3.5 font-medium transition-colors">
                {slide?.cta_text || "Смотреть коллекцию"} <ArrowRight size={18} />
              </Link>
              <Link to="/catalog" className="inline-flex items-center border-2 border-[#0F1720] text-[#0F1720] rounded-full px-7 py-3.5 font-medium hover:bg-[#0F1720] hover:text-white transition-colors">
                Весь каталог
              </Link>
            </div>
            {slides.length > 1 && (
              <div className="flex gap-2 mt-8">
                {slides.map((_, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`h-1.5 rounded-full transition-all ${i === active ? "w-8 bg-[#0E7C86]" : "w-4 bg-slate-300"}`} aria-label={`Слайд ${i + 1}`} />
                ))}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl shadow-slate-300/50">
              {slide?.image && <img src={fileUrl(slide.image)} alt={slide?.title} className="w-full h-full object-cover" />}
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-5 border border-slate-100">
              <div className="text-3xl font-display font-800 text-[#0F1720]">18+ лет</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">на рынке ПВХ-панелей</div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee items={categories.map((c) => c.name)} />

      {/* CATALOG */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0E7C86]">Каталог</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-[#0F1720] mt-2">Популярные панели</h2>
          </div>
          <Link to="/catalog" className="hidden sm:inline-flex items-center gap-2 text-[#E85D2A] font-medium hover:gap-3 transition-all">Все товары <ArrowRight size={18} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p) => <ProductCard key={p.id} product={p} categoryName={catName(p.category_id)} />)}
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Factory, t: "Собственное производство", d: "Полный цикл в Миассе — контроль качества на каждом этапе." },
              { icon: ShieldCheck, t: "Гарантия качества", d: "Влагостойкие панели с сертификатами и долгим сроком службы." },
              { icon: Star, t: "Прямые цены завода", d: "Без посредников — выгодные цены для розницы и опта." },
            ].map((a, i) => (
              <div key={i} className="bg-white rounded-xl p-7 border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-[#E85D2A]/10 text-[#E85D2A] flex items-center justify-center mb-4"><a.icon size={24} /></div>
                <h3 className="font-display font-semibold text-lg text-[#0F1720]">{a.t}</h3>
                <p className="text-slate-500 text-sm mt-2 leading-relaxed">{a.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0E7C86]">Услуги</span>
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-[#0F1720] mt-2">Всё для вашего заказа</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s) => {
            const Icon = ICONS[s.icon] || Wrench;
            return (
              <Link key={s.id} to="/services" className="group bg-white rounded-xl border border-slate-100 p-6 hover:border-[#E85D2A]/30 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-[#0E7C86]/10 text-[#0E7C86] flex items-center justify-center mb-4 group-hover:bg-[#0E7C86] group-hover:text-white transition-colors"><Icon size={24} /></div>
                <h3 className="font-display font-semibold text-[#0F1720]">{s.title}</h3>
                <p className="text-slate-500 text-sm mt-2 line-clamp-2">{s.short_description}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* NEWS */}
      {news.length > 0 && (
        <section className="bg-slate-50 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0E7C86]">Новости</span>
                <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-[#0F1720] mt-2">Последние события</h2>
              </div>
              <Link to="/news" className="hidden sm:inline-flex items-center gap-2 text-[#E85D2A] font-medium hover:gap-3 transition-all">Все новости <ArrowRight size={18} /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {news.map((n) => (
                <Link key={n.id} to={`/news/${n.slug || n.id}`} className="group bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-lg transition-all">
                  <div className="aspect-[16/9] overflow-hidden bg-slate-100">{n.cover && <img src={fileUrl(n.cover)} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                  <div className="p-5">
                    <div className="text-xs text-slate-400 mb-2">{new Date(n.created_at).toLocaleDateString("ru-RU")}</div>
                    <h3 className="font-display font-semibold text-[#0F1720] group-hover:text-[#E85D2A] transition-colors line-clamp-2">{n.title}</h3>
                    <p className="text-slate-500 text-sm mt-2 line-clamp-2">{n.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#0F1720] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold">Нужна консультация?</h2>
            <p className="text-slate-400 mt-3 max-w-xl">Оставьте заявку — рассчитаем стоимость и подберём панели под ваш проект.</p>
          </div>
          <Link to="/contacts" className="shrink-0 inline-flex items-center gap-2 bg-[#E85D2A] hover:bg-[#D04A1B] text-white rounded-full px-8 py-4 font-medium transition-colors">Связаться с нами <ArrowRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
